import React, { useContext } from "react";
import ShapeFlowObject from "../../model/ShapeFlowObject";
import { GradientDef } from "../../ai/styles/themeDecorations";
import { getShapeDefinition } from "../../model/ShapeDefinitions";
import { applyRatio, rotationToDeg } from "./helpers";
import { MMOOptions } from "./mmo-interactor";
import { DELEGATE, SET_POSITION } from '../fsm/types'
import { MachineContext } from "../fsm/useMachine";



const Rect: React.FC<{ width: number, height: number }> = ({ width, height }) => (
    <rect width={width} height={height} />
)

const Circle: React.FC<{ radius: number }> = ({ radius }) => (
    <ellipse
        cx={radius}
        cy={radius}
        rx={radius}
        ry={radius}
    />
)

const RoundedRect: React.FC<{ width: number, height: number, cornerRadius: number }> = ({ width, height, cornerRadius }) => (
    <rect
        width={width}
        height={height}
        rx={cornerRadius}
        ry={cornerRadius}
    />
)

/** Generic polygon: scales normalized 0-1 coords to pixel dimensions */
const GenericPolygon: React.FC<{ pointsArrayString: string, width: number, height: number }> = ({ pointsArrayString, width, height }) => {
    const points = pointsArrayString
        .split(" ")
        .map(pair => {
            const [x, y] = pair.split(",");
            return `${parseFloat(x) * width},${parseFloat(y) * height}`;
        })
        .join(" ");
    return <polygon points={points} />;
}

/** SVG path renderer for complex shapes like ring/donut, scales normalized 0-1 path to pixel dimensions */
const GenericPath: React.FC<{ pathString: string, width: number, height: number }> = ({ pathString, width, height }) => {
    const scaled = pathString.replace(
        /(\d+\.?\d*)\s+(\d+\.?\d*)/g,
        (_match, x, y) => `${parseFloat(x) * width} ${parseFloat(y) * height}`
    );
    return <path d={scaled} fillRule="evenodd" />;
}


/**
 * Renders SVG gradient <defs> when a gradientDef is present on the shape
 */
const GradientDefs: React.FC<{ id: string; gradient: GradientDef }> = ({ id, gradient }) => {
    const gradientId = `grad-${id}`;
    const opacity = gradient.opacity ?? 1;

    if (gradient.type === 'radial') {
        const posMap: Record<string, { cx: string; cy: string }> = {
            'center': { cx: '50%', cy: '50%' },
            'upper-left': { cx: '0%', cy: '0%' },
            'upper-right': { cx: '100%', cy: '0%' },
            'lower-left': { cx: '0%', cy: '100%' },
            'lower-right': { cx: '100%', cy: '100%' },
        };
        const pos = posMap[gradient.position || 'center'] || posMap['center'];
        return (
            <defs>
                <radialGradient id={gradientId} cx={pos.cx} cy={pos.cy} r="70.71%">
                    {gradient.stops.map((stop, i) => (
                        <stop key={i} offset={`${stop.offset}%`} stopColor={stop.color} stopOpacity={opacity} />
                    ))}
                </radialGradient>
            </defs>
        );
    }

    // Linear gradient, convert angle to SVG coordinates
    const angle = gradient.angle ?? 0;
    const rad = (angle * Math.PI) / 180;
    const x1 = 50 - Math.sin(rad) * 50;
    const y1 = 50 - Math.cos(rad) * 50;
    const x2 = 50 + Math.sin(rad) * 50;
    const y2 = 50 + Math.cos(rad) * 50;

    return (
        <defs>
            <linearGradient
                id={gradientId}
                x1={`${x1}%`} y1={`${y1}%`}
                x2={`${x2}%`} y2={`${y2}%`}
            >
                {gradient.stops.map((stop, i) => (
                    <stop key={i} offset={`${stop.offset}%`} stopColor={stop.color} stopOpacity={opacity} />
                ))}
            </linearGradient>
        </defs>
    );
};

export interface ShapeViewOptions {
    mmo: ShapeFlowObject
}

export const ShapeView: React.FC<ShapeViewOptions & MMOOptions> = React.forwardRef((props, ref) => {
    const { mmo, size,  setShowTransformer } = props
    // FIX: Calculate scaledObjects inline like AVMediaView instead of using useState
    // The useState + useEffect pattern wasn't detecting mmo.frame changes properly
    const scaledObjects = applyRatio({
        left: mmo.frame.x,
        top: mmo.frame.y,
        width: mmo.frame.width,
        height: mmo.frame.height,
    }, size.ratio)

    const w = clamp_at_1(scaledObjects.width);
    const h = clamp_at_1(scaledObjects.height);

    function shape(mmo: ShapeFlowObject) {
        switch (mmo.shapeType) {
            case "rectangle":
                return mmo.cornerRadius > 0
                    ? <RoundedRect width={w} height={h} cornerRadius={mmo.cornerRadius * size.ratio} />
                    : <Rect width={w} height={h} />
            case "roundedRectangle":
                return <RoundedRect
                    width={w}
                    height={h}
                    cornerRadius={mmo.cornerRadius > 0 ? mmo.cornerRadius * size.ratio : (scaledObjects.width * 5) / 28}
                />
            case "ellipse":
                return <Circle radius={scaledObjects.width / 2} />
            default: {
                // Check registry for path-based shapes (ring/donut)
                const def = getShapeDefinition(mmo.shapeType);
                if (def?.renderType === 'path' && def.pathString) {
                    return <GenericPath pathString={def.pathString} width={w} height={h} />;
                }
                // All polygon shapes (triangles, arrows, stars, etc.)
                if (mmo.pointsArrayString) {
                    return <GenericPolygon pointsArrayString={mmo.pointsArrayString} width={w} height={h} />;
                }
                return <Rect width={w} height={h} />;
            }
        }
    }

    // Extract opacity from color for CSS opacity (better html2canvas support)
    const shapeOpacity = mmo.gradientDef ? 1 : mmo.color.a;

    return (
        <div
            style={{
                height: clamp_at_1(scaledObjects.height),
                width: clamp_at_1(scaledObjects.width),
                position: "absolute",
                top: scaledObjects.top,
                left: scaledObjects.left,
                transform: `rotate(${rotationToDeg(mmo.rotation)}deg)`,
                opacity: shapeOpacity,
            }}
            className="mmo"
            {...props}
            //@ts-ignore
            ref={props.forwardedRef}
            id={mmo.id.replace(/-/g, "")}
            onClick={(e) => {
                    setShowTransformer(true)
                    props.onClick(e)
                }
            }
            onMouseEnter={(e) => {
                    props.onMouseEnter(e)
                }
            }
            onMouseLeave={(e) => props.onMouseLeave(e)}
        >
            <svg
                width="100%"
                height="100%"
                viewBox={`0 0 ${scaledObjects.width} ${scaledObjects.height}`}
                preserveAspectRatio="none"
                style={{
                    // Use solid color - opacity is applied to wrapper div for html2canvas compatibility
                    fill: mmo.gradientDef
                        ? `url(#grad-${mmo.id.replace(/-/g, "")})`
                        : `rgb(${mmo.color.r},${mmo.color.g},${mmo.color.b})`,
                    stroke: mmo.borderWidth > 0 ? mmo.borderColor.asRgba() : "none",
                    strokeWidth: mmo.borderWidth > 0 ? mmo.borderWidth * size.ratio : 0,
                    overflow: "visible",
                    top: scaledObjects.top,
                    left: scaledObjects.left,
                    transform: mmo.shapeType === "rectangle" ? `rotate(${rotationToDeg(mmo.rotation)}deg)` : "none"
                }}
            >
                {mmo.gradientDef && (
                    <GradientDefs id={mmo.id.replace(/-/g, "")} gradient={mmo.gradientDef} />
                )}
                {
                    shape(mmo)
                }
            </svg>
            {/* Loading indicator for AI image placeholders */}
            {mmo.__isImagePlaceholder && (
                <div className="ai-image-placeholder-loading">
                    <span className="ai-image-placeholder-text">Loading image...</span>
                </div>
            )}
        </div>
    )
})

const clamp_at_1 = (number: number) => {
    return number > 1 ? number : 1
}
